import React, { useState, useEffect } from 'react';
import './UsersManagement.css';
import NavButtons from './NavButtons';

const UsersManagement = ({ user, navigateTo }) => {
  const [activeMembers, setActiveMembers] = useState([]);
  const [deactivatedMembers, setDeactivatedMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showAddForm, setShowAddForm] = useState(false);
  const [showEditForm, setShowEditForm] = useState(false);
  const [editMember, setEditMember] = useState(null);
  const [showInactive, setShowInactive] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    const fetchMembers = async () => {
      try {
        const token = localStorage.getItem('memberToken');
        const response = await fetch(`${process.env.REACT_APP_API_URL}/api/members/members`, {
          headers: { 'Authorization': `Bearer ${token}` },
        });
        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.error || 'Failed to fetch members');
        }
        const data = await response.json();
        setActiveMembers(data.filter(m => m.active));
        setDeactivatedMembers(data.filter(m => !m.active));
        setError(null);
      } catch (err) {
        console.error('Error fetching members:', err);
        setError(err.message);
        setActiveMembers([]);
        setDeactivatedMembers([]);
      } finally {
        setLoading(false);
      }
    };
    fetchMembers();
  }, []);

  const handleAddUser = () => setShowAddForm(true);

  const handleEditUser = (memberId) => {
    if (user.role !== 'Administrator' && user._id !== memberId) {
      alert('Only Administrators can edit other users.');
      return;
    }
    const memberToEdit = activeMembers.find(m => m._id === memberId) || user;
    if (memberToEdit.role === 'SuperAdmin') {
      alert('Cannot edit SuperAdmin users.');
      return;
    }
    setEditMember(memberToEdit);
    setShowEditForm(true);
  };

  const handleDeactivateUser = async (memberId) => {
    if (user.role !== 'Administrator') {
      alert('Only Administrators can deactivate users.');
      return;
    }
    const member = activeMembers.find(m => m._id === memberId);
    if (member.role === 'SuperAdmin') {
      alert('Cannot deactivate SuperAdmin users.');
      return;
    }
    try {
      const token = localStorage.getItem('memberToken');
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/members/members/${memberId}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ active: false }),
      });
      if (!response.ok) throw new Error((await response.json()).error);
      const updatedMember = await response.json();
      setActiveMembers(activeMembers.filter(m => m._id !== memberId));
      setDeactivatedMembers([...deactivatedMembers, updatedMember]);
      console.log(`Deactivated user ${memberId}`);

      const remainingInactiveMembers = [...deactivatedMembers, updatedMember].length - 1;
      if (showInactive && remainingInactiveMembers === 0) {
        setShowInactive(false);
      }
    } catch (err) {
      console.error('Error deactivating user:', err);
      setError('Failed to deactivate user');
    }
  };

  const handleReactivateUser = async (memberId) => {
    if (user.role !== 'Administrator') {
      alert('Only Administrators can reactivate users.');
      return;
    }
    const member = deactivatedMembers.find(m => m._id === memberId);
    if (member.role === 'SuperAdmin') {
      alert('Cannot reactivate SuperAdmin users.');
      return;
    }
    try {
      const token = localStorage.getItem('memberToken');
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/members/members/${memberId}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ active: true }),
      });
      if (!response.ok) throw new Error((await response.json()).error);
      const updatedMember = await response.json();
      setDeactivatedMembers(deactivatedMembers.filter(m => m._id !== memberId));
      setActiveMembers([...activeMembers, updatedMember]);
      console.log(`Reactivated user ${memberId}`);

      const remainingInactiveMembers = deactivatedMembers.filter(m => m._id !== memberId).length;
      if (showInactive && remainingInactiveMembers === 0) {
        setShowInactive(false);
      }
    } catch (err) {
      console.error('Error reactivating user:', err);
      setError('Failed to reactivate user');
    }
  };

  const handleAddSubmit = async (e) => {
    e.preventDefault();
    const formData = new FormData(e.target);
    const newUser = {
      username: formData.get('username'),
      password: formData.get('password'),
      name: formData.get('name'),
      surname: formData.get('surname'),
      email: formData.get('email'),
      position: formData.get('position'),
      role: formData.get('role'),
    };
    if (newUser.role === 'SuperAdmin') {
      alert('Cannot assign SuperAdmin role.');
      return;
    }
    try {
      const token = localStorage.getItem('memberToken');
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/members/members`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(newUser),
      });
      if (!response.ok) throw new Error((await response.json()).error);
      const addedMember = await response.json();
      setActiveMembers([...activeMembers, addedMember]);
      setShowAddForm(false);
      console.log(`Added user ${addedMember.username}`);
    } catch (err) {
      console.error('Error adding user:', err);
      setError('Failed to add user');
    }
  };

  const handleEditSubmit = async (e) => {
    e.preventDefault();
    const formData = new FormData(e.target);
    const updatedUser = {
      username: formData.get('username'),
      password: formData.get('password') || undefined,
      name: formData.get('name'),
      surname: formData.get('surname'),
      email: formData.get('email'),
      position: formData.get('position'),
      role: user.role === 'Administrator' ? formData.get('role') : undefined,
    };
    if (updatedUser.role === 'SuperAdmin') {
      alert('Cannot assign SuperAdmin role.');
      return;
    }
    try {
      const token = localStorage.getItem('memberToken');
      const url = user.role === 'Administrator' && editMember._id !== user._id
        ? `${process.env.REACT_APP_API_URL}/api/members/members/${editMember._id}`
        : `${process.env.REACT_APP_API_URL}/api/members/me`;
      const response = await fetch(url, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(updatedUser),
      });
      if (!response.ok) throw new Error((await response.json()).error);
      const updatedMember = await response.json();
      if (editMember._id === user._id) {
        setActiveMembers(activeMembers.map(m => m._id === updatedMember._id ? updatedMember : m));
      } else {
        setActiveMembers(activeMembers.map(m => m._id === updatedMember._id ? updatedMember : m));
      }
      setShowEditForm(false);
      console.log(`Updated user ${updatedMember.username}`);
    } catch (err) {
      console.error('Error editing user:', err);
      setError('Failed to edit user');
    }
  };

  const canDeactivate = (memberId) => {
    if (user.role !== 'Administrator') return false;
    const admins = activeMembers.filter(m => m.role === 'Administrator' && m.active);
    return admins.length > 1 || (admins.length === 1 && admins[0]._id !== memberId);
  };

  const hasOppositeUsers = showInactive
    ? activeMembers.length > 0
    : deactivatedMembers.length > 0;

  const handleToggleUsers = () => {
    if (hasOppositeUsers) {
      setShowInactive((prev) => !prev);
    }
  };

  const normalizeText = (text) => {
    const smallCapsMap = {
      'ᴛ': 't',
      'ʜ': 'h',
      'ᴇ': 'e',
      'ʙ': 'b',
      'ᴜ': 'u',
      'ʀ': 'r',
      'ɢ': 'g',
      'ᴀ': 'a',
      'ᴘ': 'p',
      'ᴡ': 'w',
    };
    return text
      ? text
          .normalize('NFKD')
          .replace(/[\u0300-\u036f]/g, '')
          .replace(/[\u{1F300}-\u{1F9FF}]/gu, '')
          .split('')
          .map(char => smallCapsMap[char] || char)
          .join('')
          .toLowerCase()
      : '';
  };

  const displayedMembers = (showInactive ? deactivatedMembers : activeMembers).filter(member => {
    if (!searchQuery) return true;
    const query = normalizeText(searchQuery);
    const fullName = normalizeText(`${member.name || ''} ${member.surname || ''}`);
    const email = normalizeText(member.email || '');
    return fullName.includes(query) || email.includes(query);
  });

  if (user.role === 'Light') {
    return (
      <div className="users-management">
        <NavButtons navigateTo={navigateTo} user={user} activePage="users" />
        <p className="error">Access denied for Light users.</p>
      </div>
    );
  }

  return (
    <div className="users-management">
      <NavButtons navigateTo={navigateTo} user={user} activePage="users" />
      {loading ? (
        <div className="loading"><div className="spinner"></div></div>
      ) : error ? (
        <p className="error">{error}</p>
      ) : (
        <>
          <div className="user-actions">
            {user.role === 'Administrator' && (
              <button onClick={handleAddUser} className="manage-deals">Add User</button>
            )}
            {user.role === 'Member' && (
              <button onClick={() => handleEditUser(user._id)} className="edit-profile">Edit My Profile</button>
            )}
            <button
              onClick={handleToggleUsers}
              className={`toggle-users ${!hasOppositeUsers ? 'disabled' : ''}`}
              disabled={!hasOppositeUsers}
            >
              {showInactive ? 'Active Members' : 'Inactive Members'}
            </button>
            <input
              type="text"
              placeholder="Search users by name or email..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="search-bar"
            />
          </div>
          <h3>{showInactive ? 'Deactivated Users' : 'Active Users'}</h3>
          <table>
            <thead>
              <tr>
                <th className="name-col">Name</th>
                <th className="email-col">Email</th>
                <th className="role-col">Role</th>
                <th className="login-col">Last Login</th>
                <th className="actions-col">Actions</th>
              </tr>
            </thead>
            <tbody>
              {displayedMembers.map(member => (
                <tr key={member._id}>
                  <td className="name-col">{member.name} {member.surname}</td>
                  <td className="email-col">{member.email}</td>
                  <td className="role-col">{member.role}</td>
                  <td className="login-col">{member.lastLogin ? new Date(member.lastLogin).toLocaleDateString('en-GB') + ' ' + new Date(member.lastLogin).toLocaleTimeString() : 'Never'}</td>
                  <td className="actions-column">
                    {member.role !== 'SuperAdmin' && (
                      <>
                        <button onClick={() => handleEditUser(member._id)} className="action-button edit">Edit</button>
                        {user.role === 'Administrator' && (
                          showInactive ? (
                            <button onClick={() => handleReactivateUser(member._id)} className="action-button reactivate">Reactivate</button>
                          ) : (
                            <button
                              onClick={() => handleDeactivateUser(member._id)}
                              disabled={!canDeactivate(member._id)}
                              className="action-button deactivate"
                            >
                              Deactivate
                            </button>
                          )
                        )}
                      </>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </>
      )}

      {showAddForm && (
        <div className="modal">
          <div className="modal-content auth-form">
            <h3 className="deal-tile-center">Add New User</h3>
            <form onSubmit={handleAddSubmit}>
              <div className="form-group">
                <label>Username <input type="text" name="username" required /></label>
              </div>
              <div className="form-group">
                <label>Password <input type="password" name="password" required /></label>
              </div>
              <div className="form-group">
                <label>Name <input type="text" name="name" /></label>
              </div>
              <div className="form-group">
                <label>Surname <input type="text" name="surname" /></label>
              </div>
              <div className="form-group">
                <label>Email <input type="email" name="email" /></label>
              </div>
              <div className="form-group">
                <label>Position <input type="text" name="position" /></label>
              </div>
              <div className="form-group">
                <label>Role
                  <select name="role" defaultValue="Member">
                    <option value="Administrator">Administrator</option>
                    <option value="Member">Member</option>
                    <option value="Light">Light</option>
                  </select>
                </label>
              </div>
              <div className="button-group">
                <button type="submit">Add</button>
                <button type="button" onClick={() => setShowAddForm(false)}>Cancel</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {showEditForm && editMember && (
        <div className="modal">
          <div className="modal-content auth-form">
            <h3 className="deal-tile-center">Edit User</h3>
            <form onSubmit={handleEditSubmit}>
              <div className="form-group">
                <label>Username <input type="text" name="username" defaultValue={editMember.username} required /></label>
              </div>
              <div className="form-group">
                <label>Password <input type="password" name="password" placeholder="Leave blank to keep unchanged" /></label>
              </div>
              <div className="form-group">
                <label>Name <input type="text" name="name" defaultValue={editMember.name} /></label>
              </div>
              <div className="form-group">
                <label>Surname <input type="text" name="surname" defaultValue={editMember.surname} /></label>
              </div>
              <div className="form-group">
                <label>Email <input type="email" name="email" defaultValue={editMember.email} /></label>
              </div>
              <div className="form-group">
                <label>Position <input type="text" name="position" defaultValue={editMember.position} /></label>
              </div>
              {user.role === 'Administrator' && (
                <div className="form-group">
                  <label>Role
                    <select name="role" defaultValue={editMember.role}>
                      <option value="Administrator">Administrator</option>
                      <option value="Member">Member</option>
                      <option value="Light">Light</option>
                    </select>
                  </label>
                </div>
              )}
              <div className="button-group">
                <button type="submit">Save</button>
                <button type="button" onClick={() => setShowEditForm(false)}>Cancel</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default UsersManagement;